import { useEffect, useState } from 'react'
import { collection, doc, limit, orderBy, query, serverTimestamp } from 'firebase/firestore'
import { addDoc, deleteDoc, onSnapshot, setDoc, updateDoc } from '@/lib/metered'
import { db } from '@/lib/firebase'
import type { CommentParentKind } from '@/lib/comments'

export type Notification = {
  id: string
  /**
   * A mention is somebody asking you something. A reaction is somebody acknowledging
   * what you wrote. The difference is what you may do with it: see `clearable`.
   */
  kind: 'mention' | 'reaction'
  /** The reaction that was added. Only on `kind: 'reaction'`. */
  emoji?: string
  /**
   * What was commented on. The bell routes on this through `urlForNotification`, which
   * refuses anything it does not recognise: notifications written before September 2026
   * can say `field` for a product, so an unrecognised or missing value opens nothing
   * rather than opening the wrong record.
   */
  itemType: CommentParentKind
  itemId: string
  itemRef: string
  itemTitle: string
  /**
   * The comment this is about, so the bell can land on it rather than on the top of a
   * discussion dozens of comments long. Absent on anything written before September 2026,
   * which still opens the item itself.
   */
  commentId?: string | null
  body: string
  byEmail: string
  byName: string | null
  read: boolean
  at: { seconds: number } | null
}

export function useNotifications(uid: string | undefined) {
  const [items, setItems] = useState<Notification[]>([])

  useEffect(() => {
    if (!uid) { setItems([]); return }
    return onSnapshot(
      query(collection(db, 'users', uid, 'notifications'), orderBy('at', 'desc'), limit(50)),
      (snap) => setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Notification)),
      () => setItems([]),
    )
  }, [uid])

  return { items, unread: items.filter((n) => !n.read).length }
}

export async function notify(
  uid: string,
  payload: Omit<Notification, 'id' | 'read' | 'at'>,
) {
  await addDoc(collection(db, 'users', uid, 'notifications'), {
    ...payload, read: false, at: serverTimestamp(),
  })
}

/**
 * Whether the recipient may sweep this one away, mirroring `firestore.rules`.
 *
 * A mention stays until it is dealt with, because nothing else in the tracker lists what
 * you were asked — clearing one loses the question. A reaction has said everything it has
 * to say the moment you have seen it. Anything written before `kind` existed reads as a
 * mention, because absent is not permission.
 */
export const clearable = (n: Pick<Notification, 'kind'>) => n.kind === 'reaction'

export async function clearNotification(uid: string, id: string) {
  await deleteDoc(doc(db, 'users', uid, 'notifications', id))
}

/**
 * The same notification, at an id the sender can work out again.
 *
 * A reaction that is taken back has to take its notification with it, and the person
 * taking it back cannot read the recipient's list to find it. Writing it at a derived
 * address is what makes the retraction possible without that.
 */
export async function notifyAt(
  uid: string, id: string, payload: Omit<Notification, 'id' | 'read' | 'at'>,
) {
  await setDoc(doc(db, 'users', uid, 'notifications', id), {
    ...payload, read: false, at: serverTimestamp(),
  })
}

export async function markRead(uid: string, id: string) {
  await updateDoc(doc(db, 'users', uid, 'notifications', id), { read: true })
}

export async function markAllRead(uid: string, ids: string[]) {
  await Promise.all(ids.map((id) => markRead(uid, id)))
}
